<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Header -->
      <!-- <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 flex items-center">
          Calendar
          <span
            class="ml-3 text-sm font-normal text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm"
          >
            {{ currentMonthName }} {{ currentYear }}
          </span>
        </h1>
        <p class="text-lg text-gray-600 mt-1">
          Schedule, track, and manage verification tasks
        </p>
      </div> -->

      <!-- Calendar Controls -->
      <div
        class="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-6"
      >
        <div
          class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        >
          <!-- View Toggle -->
          <div class="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
            <button
              v-for="view in views"
              :key="view.value"
              @click="currentView = view.value"
              class="px-4 py-2 text-sm font-medium rounded-md transition-all"
              :class="[
                currentView === view.value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              ]"
            >
              {{ view.label }}
            </button>
          </div>

          <!-- Navigation -->
          <div class="flex items-center space-x-4">
            <button
              @click="today"
              class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Today
            </button>
            <div class="flex items-center space-x-2">
              <button
                @click="previousPeriod"
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                @click="nextPeriod"
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Filters and Actions -->
          <div class="flex items-center space-x-3">
            <select
              v-model="filterOfficer"
              class="pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Officers</option>
              <option
                v-for="officer in officers"
                :key="officer.id"
                :value="officer.id"
              >
                {{ officer.name }}
              </option>
            </select>

            <select
              v-model="filterType"
              class="pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="verification">Verification</option>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="deadline">Deadline</option>
            </select>

            <button
              @click="openAddEventModal"
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              ADD EVENT
            </button>
          </div>
        </div>
      </div>

      <!-- Calendar Views -->
      <div
        class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <!-- Month View -->
        <div v-if="currentView === 'month'" class="p-6">
          <!-- Weekday Headers -->
          <div class="grid grid-cols-7 gap-2 mb-4">
            <div
              v-for="day in weekdays"
              :key="day"
              class="text-center text-sm font-semibold text-gray-500 py-2"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Grid -->
          <div class="grid grid-cols-7 gap-2">
            <div
              v-for="(day, index) in monthDays"
              :key="index"
              class="min-h-32 border border-gray-100 rounded-lg p-2 hover:border-blue-200 transition-colors"
              :class="[
                day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                day.isToday ? 'ring-2 ring-blue-500 ring-offset-2' : '',
              ]"
              @click="selectDate(day.date)"
            >
              <!-- Date Header -->
              <div class="flex items-center justify-between mb-2">
                <span
                  class="text-sm font-medium"
                  :class="[
                    day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400',
                    day.isToday ? 'text-blue-600' : '',
                  ]"
                >
                  {{ day.date }}
                </span>
                <span
                  v-if="getEventCount(day.date) > 0"
                  class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full"
                >
                  {{ getEventCount(day.date) }}
                </span>
              </div>

              <!-- Events Preview -->
              <div class="space-y-1">
                <div
                  v-for="event in getDayEvents(day.date).slice(0, 2)"
                  :key="event.id"
                  class="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                  :class="eventClasses[event.type]"
                  @click.stop="openEventDetails(event)"
                >
                  {{ event.time }} {{ event.title }}
                </div>
                <div
                  v-if="getDayEvents(day.date).length > 2"
                  class="text-xs text-gray-500 pl-1"
                >
                  +{{ getDayEvents(day.date).length - 2 }} more
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Week View -->
        <div v-if="currentView === 'week'" class="p-6">
          <!-- Week View Header -->
          <div class="grid grid-cols-8 gap-2 mb-4">
            <div class="text-center text-sm font-semibold text-gray-500 py-2">
              Time
            </div>
            <div v-for="day in weekDays" :key="day.date" class="text-center">
              <div class="text-sm font-semibold text-gray-700">
                {{ day.name }}
              </div>
              <div
                class="text-sm mt-1 inline-block px-2 py-1 rounded-full"
                :class="
                  day.isToday
                    ? 'bg-blue-100 text-blue-600 font-medium'
                    : 'text-gray-500'
                "
              >
                {{ day.date }}
              </div>
            </div>
          </div>

          <!-- Time Slots -->
          <div class="space-y-2">
            <div
              v-for="hour in workHours"
              :key="hour"
              class="grid grid-cols-8 gap-2 min-h-16"
            >
              <div
                class="text-xs text-gray-500 flex items-center justify-end pr-2"
              >
                {{ formatHour(hour) }}
              </div>
              <div
                v-for="day in weekDays"
                :key="day.date"
                class="border border-gray-100 rounded-lg p-1 hover:border-blue-200 transition-colors cursor-pointer"
                @click="openAddEventModal(day.date, hour)"
              >
                <div
                  v-for="event in getWeekEvents(day.date, hour)"
                  :key="event.id"
                  class="text-xs p-1 mb-1 rounded truncate"
                  :class="eventClasses[event.type]"
                  @click.stop="openEventDetails(event)"
                >
                  {{ event.title }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Day View -->
        <div v-if="currentView === 'day'" class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-semibold text-gray-900">
              {{ formatSelectedDate(selectedDate) }}
            </h2>
            <button
              @click="openAddEventModal(selectedDate)"
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              Add Event
            </button>
          </div>

          <!-- Timeline -->
          <div class="space-y-2">
            <div
              v-for="hour in allHours"
              :key="hour"
              class="flex items-start min-h-16 group"
            >
              <div class="w-20 text-xs text-gray-500 pt-2">
                {{ formatHour(hour) }}
              </div>
              <div class="flex-1 border-l border-gray-200 pl-4 relative">
                <!-- Events -->
                <div
                  v-for="event in getDayEventsAtHour(selectedDate, hour)"
                  :key="event.id"
                  class="mb-2 p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                  :class="eventClasses[event.type]"
                  @click="openEventDetails(event)"
                >
                  <div class="flex items-center justify-between">
                    <span class="font-medium">{{ event.title }}</span>
                    <span class="text-xs opacity-75">{{ event.time }}</span>
                  </div>
                  <p class="text-sm mt-1 opacity-90">{{ event.description }}</p>
                  <div class="flex items-center mt-2 text-xs">
                    <img
                      :src="event.officer.avatar"
                      :alt="event.officer.name"
                      class="w-5 h-5 rounded-full mr-2"
                    />
                    <span>{{ event.officer.name }}</span>
                  </div>
                </div>

                <!-- Empty state -->
                <div
                  v-if="getDayEventsAtHour(selectedDate, hour).length === 0"
                  class="text-sm text-gray-400 py-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  @click="openAddEventModal(selectedDate, hour)"
                >
                  + Click to add event
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upcoming Events Sidebar (for month/week view) -->
      <div
        v-if="currentView !== 'day'"
        class="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8"
      >
        <!-- Upcoming Events -->
        <div
          class="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Upcoming Events
          </h3>
          <div class="space-y-4">
            <div
              v-for="event in upcomingEvents"
              :key="event.id"
              class="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
              @click="openEventDetails(event)"
            >
              <div
                class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                :class="eventClasses[event.type]"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="eventIcons[event.type]"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <h4 class="text-sm font-medium text-gray-900">
                    {{ event.title }}
                  </h4>
                  <span class="text-xs text-gray-500">{{ event.time }}</span>
                </div>
                <p class="text-sm text-gray-600 mt-1">
                  {{ event.description }}
                </p>
                <div class="flex items-center mt-2">
                  <img
                    :src="event.officer.avatar"
                    :alt="event.officer.name"
                    class="w-5 h-5 rounded-full mr-2"
                  />
                  <span class="text-xs text-gray-500">{{
                    event.officer.name
                  }}</span>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-xs text-gray-500">{{ event.date }}</span>
                </div>
              </div>
              <StatusBadge v-if="event.status" :status="event.status" />
            </div>
          </div>
        </div>

        <!-- Mini Calendar -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Quick Navigation
          </h3>

          <!-- Mini Month Selector -->
          <div class="flex items-center justify-between mb-4">
            <button
              @click="miniPreviousMonth"
              class="p-1 hover:bg-gray-100 rounded"
            >
              <svg
                class="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span class="text-sm font-medium text-gray-700"
              >{{ miniMonthName }} {{ miniYear }}</span
            >
            <button
              @click="miniNextMonth"
              class="p-1 hover:bg-gray-100 rounded"
            >
              <svg
                class="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <!-- Mini Weekdays -->
          <div
            class="grid grid-cols-7 gap-1 mb-2 text-center text-xs font-medium text-gray-500"
          >
            <div v-for="day in ['M', 'T', 'W', 'T', 'F', 'S', 'S']" :key="day">
              {{ day }}
            </div>
          </div>

          <!-- Mini Days -->
          <div class="grid grid-cols-7 gap-1">
            <div
              v-for="(day, index) in miniMonthDays"
              :key="index"
              class="text-center py-1 text-sm cursor-pointer hover:bg-gray-100 rounded"
              :class="[
                day.isCurrentMonth ? 'text-gray-700' : 'text-gray-300',
                day.isToday ? 'bg-blue-100 text-blue-600 font-medium' : '',
                day.date === selectedDay ? 'ring-2 ring-blue-500' : '',
              ]"
              @click="jumpToDate(day.fullDate)"
            >
              {{ day.date }}
            </div>
          </div>

          <!-- Legend -->
          <div class="mt-6 space-y-2">
            <div class="text-xs font-medium text-gray-500 mb-2">
              Event Types
            </div>
            <div
              v-for="(type, key) in eventTypeLabels"
              :key="key"
              class="flex items-center"
            >
              <span
                :class="['w-3 h-3 rounded-full mr-2', eventDotClasses[key]]"
              ></span>
              <span class="text-xs text-gray-600">{{ type }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Details Modal -->
      <div
        v-if="showEventModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeEventModal"
          ></div>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
            >&#8203;</span
          >

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center">
                  <div
                    class="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                    :class="
                      selectedEvent
                        ? eventClasses[selectedEvent.type]
                        : 'bg-gray-100'
                    "
                  >
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        :d="selectedEvent ? eventIcons[selectedEvent.type] : ''"
                      />
                    </svg>
                  </div>
                  <h3
                    class="text-lg font-semibold text-gray-900"
                    id="modal-title"
                  >
                    {{ selectedEvent?.title }}
                  </h3>
                </div>
                <StatusBadge
                  v-if="selectedEvent?.status"
                  :status="selectedEvent.status"
                />
              </div>

              <div class="space-y-4">
                <!-- Date & Time -->
                <div class="flex items-start space-x-3">
                  <svg
                    class="w-5 h-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <p class="text-sm text-gray-900">
                      {{ selectedEvent?.date }}
                    </p>
                    <p class="text-sm text-gray-500">
                      {{ selectedEvent?.time }} ({{ selectedEvent?.duration }})
                    </p>
                  </div>
                </div>

                <!-- Description -->
                <div class="flex items-start space-x-3">
                  <svg
                    class="w-5 h-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6h16M4 12h16M4 18h7"
                    />
                  </svg>
                  <p class="text-sm text-gray-600">
                    {{ selectedEvent?.description }}
                  </p>
                </div>

                <!-- Officer -->
                <div class="flex items-start space-x-3">
                  <svg
                    class="w-5 h-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <div class="flex items-center">
                    <img
                      :src="selectedEvent?.officer.avatar"
                      :alt="selectedEvent?.officer.name"
                      class="w-6 h-6 rounded-full mr-2"
                    />
                    <span class="text-sm text-gray-900">{{
                      selectedEvent?.officer.name
                    }}</span>
                  </div>
                </div>

                <!-- Related Case -->
                <div
                  v-if="selectedEvent?.caseNumber"
                  class="flex items-start space-x-3"
                >
                  <svg
                    class="w-5 h-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span class="text-sm text-blue-600 font-medium"
                    >Case #{{ selectedEvent?.caseNumber }}</span
                  >
                </div>

                <!-- Attachments -->
                <div
                  v-if="selectedEvent?.attachments?.length"
                  class="flex items-start space-x-3"
                >
                  <svg
                    class="w-5 h-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="att in selectedEvent.attachments"
                      :key="att"
                      class="text-xs bg-gray-100 px-2 py-1 rounded"
                    >
                      {{ att }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-end space-x-3">
                <button
                  v-if="selectedEvent?.caseNumber"
                  @click="goToCase(selectedEvent.caseNumber)"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  View Case
                </button>
                <button
                  @click="closeEventModal"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Event Modal -->
      <div
        v-if="showAddEventModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeAddEventModal"
          ></div>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
            >&#8203;</span
          >

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3
                class="text-lg font-semibold text-gray-900 mb-4"
                id="modal-title"
              >
                {{ editingEvent ? "Edit Event" : "Add New Event" }}
              </h3>

              <form @submit.prevent="saveEvent" class="space-y-4">
                <!-- Event Title -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Title</label
                  >
                  <input
                    v-model="eventForm.title"
                    type="text"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter event title"
                  />
                </div>

                <!-- Event Type -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Type</label
                  >
                  <select
                    v-model="eventForm.type"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="verification">Verification</option>
                    <option value="call">Call</option>
                    <option value="meeting">Meeting</option>
                    <option value="deadline">Deadline</option>
                  </select>
                </div>

                <!-- Date and Time -->
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Date</label
                    >
                    <input
                      v-model="eventForm.date"
                      type="date"
                      required
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Time</label
                    >
                    <input
                      v-model="eventForm.time"
                      type="time"
                      required
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <!-- Duration -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Duration</label
                  >
                  <select
                    v-model="eventForm.duration"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="30m">30 minutes</option>
                    <option value="1h">1 hour</option>
                    <option value="1.5h">1.5 hours</option>
                    <option value="2h">2 hours</option>
                    <option value="3h">3 hours</option>
                    <option value="full-day">Full day</option>
                  </select>
                </div>

                <!-- Officer -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Assigned Officer</label
                  >
                  <select
                    v-model="eventForm.officerId"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select officer</option>
                    <option
                      v-for="officer in officers"
                      :key="officer.id"
                      :value="officer.id"
                    >
                      {{ officer.name }}
                    </option>
                  </select>
                </div>

                <!-- Related Case -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Related Case (optional)</label
                  >
                  <input
                    v-model="eventForm.caseNumber"
                    type="text"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter case number"
                  />
                </div>

                <!-- Description -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Description</label
                  >
                  <textarea
                    v-model="eventForm.description"
                    rows="3"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter event description"
                  ></textarea>
                </div>

                <!-- Status -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Status</label
                  >
                  <select
                    v-model="eventForm.status"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    @click="closeAddEventModal"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {{ editingEvent ? "Update" : "Create" }} Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";

const router = useRouter();

// View state
const currentView = ref("month");
const views = [
  { label: "Month", value: "month" },
  { label: "Week", value: "week" },
  { label: "Day", value: "day" },
];

// Date state
const currentDate = ref(new Date());
const selectedDate = ref(new Date());
const selectedDay = ref(new Date().getDate());
const miniCurrentDate = ref(new Date());

// Filters
const filterOfficer = ref("");
const filterType = ref("");

// Modal state
const showEventModal = ref(false);
const showAddEventModal = ref(false);
const selectedEvent = ref(null);
const editingEvent = ref(null);

// Event form
const eventForm = ref({
  title: "",
  type: "verification",
  date: "",
  time: "",
  duration: "1h",
  officerId: "",
  caseNumber: "",
  description: "",
  status: "scheduled",
});

// Weekdays
const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Officers data
const officers = ref([
  {
    id: 1,
    name: "John Doe",
    avatar:
      "https://ui-avatars.com/api/?name=John+Doe&background=3b82f6&color=fff",
  },
  {
    id: 2,
    name: "Jane Smith",
    avatar:
      "https://ui-avatars.com/api/?name=Jane+Smith&background=10b981&color=fff",
  },
  {
    id: 3,
    name: "Mike Johnson",
    avatar:
      "https://ui-avatars.com/api/?name=Mike+Johnson&background=8b5cf6&color=fff",
  },
  {
    id: 4,
    name: "Sarah Williams",
    avatar:
      "https://ui-avatars.com/api/?name=Sarah+Williams&background=ec4899&color=fff",
  },
  {
    id: 5,
    name: "David Brown",
    avatar:
      "https://ui-avatars.com/api/?name=David+Brown&background=f59e0b&color=fff",
  },
]);

// Event classes and icons
const eventClasses = {
  verification: "bg-blue-500 text-white",
  call: "bg-green-500 text-white",
  meeting: "bg-purple-500 text-white",
  deadline: "bg-red-500 text-white",
};

const eventDotClasses = {
  verification: "bg-blue-500",
  call: "bg-green-500",
  meeting: "bg-purple-500",
  deadline: "bg-red-500",
};

const eventIcons = {
  verification: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  call: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  meeting:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  deadline: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
};

const eventTypeLabels = {
  verification: "Verification",
  call: "Call",
  meeting: "Meeting",
  deadline: "Deadline",
};

// Mock events data
const events = ref([
  {
    id: 1,
    title: "Case #827373 Verification",
    type: "verification",
    date: "2026-02-25",
    time: "10:00",
    duration: "1h",
    description: "Verify customer documents for case #827373",
    officer: officers.value[0],
    caseNumber: "827373",
    status: "scheduled",
    attachments: ["id_document.pdf", "proof_of_address.pdf"],
  },
  {
    id: 2,
    title: "Follow-up Call with Client",
    type: "call",
    date: "2026-02-25",
    time: "14:30",
    duration: "30m",
    description: "Schedule follow-up call to discuss verification results",
    officer: officers.value[1],
    caseNumber: "827348",
    status: "scheduled",
  },
  {
    id: 3,
    title: "Team Meeting",
    type: "meeting",
    date: "2026-02-26",
    time: "09:00",
    duration: "1.5h",
    description: "Weekly team sync to discuss pending cases",
    officer: officers.value[2],
    status: "scheduled",
  },
  {
    id: 4,
    title: "Verification Deadline",
    type: "deadline",
    date: "2026-02-27",
    time: "17:00",
    duration: "full-day",
    description: "Deadline for completing case #827229 verification",
    officer: officers.value[3],
    caseNumber: "827229",
    status: "scheduled",
  },
  {
    id: 5,
    title: "Case #827542 Review",
    type: "verification",
    date: "2026-02-25",
    time: "11:30",
    duration: "1h",
    description: "Review submitted documents for case #827542",
    officer: officers.value[4],
    caseNumber: "827542",
    status: "in-progress",
  },
  {
    id: 6,
    title: "Client Consultation",
    type: "meeting",
    date: "2026-02-28",
    time: "15:00",
    duration: "1h",
    description: "Consult with client regarding verification requirements",
    officer: officers.value[0],
    caseNumber: "827373",
    status: "scheduled",
  },
]);

// Computed properties
// const currentMonthName = computed(() => {
//   return currentDate.value.toLocaleDateString("en-US", { month: "long" });
// });

// const currentYear = computed(() => {
//   return currentDate.value.getFullYear();
// });

const miniMonthName = computed(() => {
  return miniCurrentDate.value.toLocaleDateString("en-US", { month: "long" });
});

const miniYear = computed(() => {
  return miniCurrentDate.value.getFullYear();
});

const workHours = computed(() => {
  return Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 8 PM
});

const allHours = computed(() => {
  return Array.from({ length: 24 }, (_, i) => i);
});

// Month days calculation
const monthDays = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days = [];
  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Adjust for Monday start

  // Previous month days
  for (let i = startOffset; i > 0; i--) {
    const date = new Date(year, month, 1 - i);
    days.push({
      date: date.getDate(),
      fullDate: date,
      isCurrentMonth: false,
      isToday: isSameDay(date, new Date()),
    });
  }

  // Current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({
      date: i,
      fullDate: date,
      isCurrentMonth: true,
      isToday: isSameDay(date, new Date()),
    });
  }

  // Next month days (to complete 6 rows)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date: date.getDate(),
      fullDate: date,
      isCurrentMonth: false,
      isToday: isSameDay(date, new Date()),
    });
  }

  return days;
});

// Week days for week view
const weekDays = computed(() => {
  const start = new Date(currentDate.value);
  start.setDate(start.getDate() - start.getDay() + 1); // Start from Monday

  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      name: date.toLocaleDateString("en-US", { weekday: "short" }),
      date: date.getDate(),
      fullDate: date,
      isToday: isSameDay(date, new Date()),
    });
  }
  return days;
});

// Mini month days
const miniMonthDays = computed(() => {
  const year = miniCurrentDate.value.getFullYear();
  const month = miniCurrentDate.value.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days = [];
  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

  for (let i = startOffset; i > 0; i--) {
    const date = new Date(year, month, 1 - i);
    days.push({
      date: date.getDate(),
      fullDate: date,
      isCurrentMonth: false,
      isToday: isSameDay(date, new Date()),
    });
  }

  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({
      date: i,
      fullDate: date,
      isCurrentMonth: true,
      isToday: isSameDay(date, new Date()),
    });
  }

  return days;
});

// Upcoming events
const upcomingEvents = computed(() => {
  const today = new Date();
  return events.value
    .filter((event) => new Date(event.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);
});

// Helper functions
const isSameDay = (date1, date2) => {
  return date1.toDateString() === date2.toDateString();
};

const formatHour = (hour) => {
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:00 ${ampm}`;
};

const formatSelectedDate = (date) => {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Event helpers
const getEventCount = (date) => {
  const dateStr = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth(),
    date
  )
    .toISOString()
    .split("T")[0];
  return events.value.filter((e) => e.date === dateStr).length;
};

const getDayEvents = (date) => {
  const dateStr = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth(),
    date
  )
    .toISOString()
    .split("T")[0];
  return events.value.filter((e) => e.date === dateStr);
};

const getWeekEvents = (date, hour) => {
  const dateStr = date.toISOString().split("T")[0];
  const hourStr = `${hour.toString().padStart(2, "0")}:00`;
  return events.value.filter((e) => e.date === dateStr && e.time === hourStr);
};

const getDayEventsAtHour = (date, hour) => {
  const dateStr = date.toISOString().split("T")[0];
  const hourStr = `${hour.toString().padStart(2, "0")}:00`;
  return events.value.filter((e) => e.date === dateStr && e.time === hourStr);
};

// Navigation
const previousPeriod = () => {
  if (currentView.value === "month") {
    currentDate.value.setMonth(currentDate.value.getMonth() - 1);
  } else if (currentView.value === "week") {
    currentDate.value.setDate(currentDate.value.getDate() - 7);
  } else {
    currentDate.value.setDate(currentDate.value.getDate() - 1);
  }
  currentDate.value = new Date(currentDate.value);
};

const nextPeriod = () => {
  if (currentView.value === "month") {
    currentDate.value.setMonth(currentDate.value.getMonth() + 1);
  } else if (currentView.value === "week") {
    currentDate.value.setDate(currentDate.value.getDate() + 7);
  } else {
    currentDate.value.setDate(currentDate.value.getDate() + 1);
  }
  currentDate.value = new Date(currentDate.value);
};

const today = () => {
  currentDate.value = new Date();
  selectedDate.value = new Date();
};

const selectDate = (date) => {
  selectedDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth(),
    date
  );
  if (currentView.value === "month") {
    currentView.value = "day";
  }
};

const jumpToDate = (date) => {
  currentDate.value = new Date(date);
  selectedDate.value = new Date(date);
  selectedDay.value = date.getDate();
};

const miniPreviousMonth = () => {
  miniCurrentDate.value.setMonth(miniCurrentDate.value.getMonth() - 1);
  miniCurrentDate.value = new Date(miniCurrentDate.value);
};

const miniNextMonth = () => {
  miniCurrentDate.value.setMonth(miniCurrentDate.value.getMonth() + 1);
  miniCurrentDate.value = new Date(miniCurrentDate.value);
};

// Modal handlers
const openEventDetails = (event) => {
  selectedEvent.value = event;
  showEventModal.value = true;
};

const closeEventModal = () => {
  showEventModal.value = false;
  selectedEvent.value = null;
};

const openAddEventModal = (date = null, hour = null) => {
  if (date) {
    if (date instanceof Date) {
      eventForm.value.date = date.toISOString().split("T")[0];
    } else {
      eventForm.value.date = date;
    }
  }
  if (hour) {
    eventForm.value.time = `${hour.toString().padStart(2, "0")}:00`;
  }
  showAddEventModal.value = true;
  editingEvent.value = null;
};

const closeAddEventModal = () => {
  showAddEventModal.value = false;
  editingEvent.value = null;
  eventForm.value = {
    title: "",
    type: "verification",
    date: "",
    time: "",
    duration: "1h",
    officerId: "",
    caseNumber: "",
    description: "",
    status: "scheduled",
  };
};

const saveEvent = () => {
  const officer = officers.value.find(
    (o) => o.id === parseInt(eventForm.value.officerId)
  );

  const newEvent = {
    id: editingEvent.value ? editingEvent.value.id : events.value.length + 1,
    title: eventForm.value.title,
    type: eventForm.value.type,
    date: eventForm.value.date,
    time: eventForm.value.time,
    duration: eventForm.value.duration,
    description: eventForm.value.description,
    officer: officer,
    caseNumber: eventForm.value.caseNumber || null,
    status: eventForm.value.status,
  };

  if (editingEvent.value) {
    const index = events.value.findIndex((e) => e.id === editingEvent.value.id);
    events.value[index] = newEvent;
  } else {
    events.value.push(newEvent);
  }

  closeAddEventModal();
};

const goToCase = (caseNumber) => {
  router.push(`/tickets/${caseNumber}`);
  closeEventModal();
};

// Initialize
onMounted(() => {
  // Set default form date to today
  const today = new Date();
  eventForm.value.date = today.toISOString().split("T")[0];
  eventForm.value.time = "09:00";
});
</script>
